import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

/* ---------------------------------- copy ---------------------------------- */

const CLAUSES = [
  'That I will endeavor, this day, to do good, in matters both great and small;',
  'That I will respect the time, the attention, and the independence of my fellow man, asking of others only what I would gladly give myself;',
  'That I will be patient where there is delay, gracious where there is error, and quiet where I have nothing useful to say;',
  'That I will repay the kindness here asked of me, not necessarily to its source, but surely, and without needing to be reminded;',
  'That I will hold the favor I receive not as a debt discharged, but as the standard I must meet when a favor is next asked of me.',
];

const CERT_BODY_1 =
  'having read the words in full, and finding that they could be kept, did freely take and subscribe to';
const CERT_BODY_2 =
  'and stands entitled to the favor requested, together with every consideration attendant thereto.';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ORDINALS = [
  'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh',
  'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth',
  'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth',
  'nineteenth', 'twentieth', 'twenty-first', 'twenty-second',
  'twenty-third', 'twenty-fourth', 'twenty-fifth', 'twenty-sixth',
  'twenty-seventh', 'twenty-eighth', 'twenty-ninth', 'thirtieth',
  'thirty-first',
];

/* ------------------------------ signature pad ----------------------------- */

type Point = { x: number; y: number };
type Stroke = Point[];

interface SignaturePadHandle {
  clear(): void;
  getStrokes(): Stroke[];
}

const INK = '#1A1A1A';
const PAD_LINE_WIDTH = 2.25;

const PadShell = styled.div`
  position: relative;
  height: 180px;
  background: #fbf8f2;
  border: 1px solid var(--border);

  @media (max-width: 768px) {
    height: 150px;
  }
`;

const PadCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  cursor: crosshair;
`;

const PadBaseline = styled.div`
  position: absolute;
  left: 1.5rem;
  right: 1.5rem;
  bottom: 2.75rem;
  border-bottom: 1px dashed #b9b2a9;
  pointer-events: none;
`;

const PadMark = styled.span`
  position: absolute;
  left: 1.5rem;
  bottom: 3.05rem;
  font-family: var(--font-display);
  font-size: 1.3rem;
  line-height: 1;
  color: #b9b2a9;
  pointer-events: none;
`;

const PadCaption = styled.span`
  position: absolute;
  left: 1.5rem;
  bottom: 1.05rem;
  font-family: var(--font-mono);
  font-size: 0.58rem;
  letter-spacing: 0.12em;
  color: var(--text-muted);
  pointer-events: none;
`;

const PadClear = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 1rem;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  color: var(--text-muted);
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const SignaturePad = forwardRef<
  SignaturePadHandle,
  { onInkChange: (hasInk: boolean) => void }
>(({ onInkChange }, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const sizeRef = useRef({ w: 0, h: 0 });
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const setupContext = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas ? canvas.getContext('2d') : null;
    if (!ctx) return null;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.lineWidth = PAD_LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  }, []);

  const redraw = useCallback(() => {
    const ctx = setupContext();
    if (!ctx) return;
    ctx.clearRect(0, 0, sizeRef.current.w, sizeRef.current.h);
    for (const stroke of strokesRef.current) {
      if (stroke.length === 1) {
        ctx.beginPath();
        ctx.arc(stroke[0].x, stroke[0].y, PAD_LINE_WIDTH / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x, stroke[i].y);
        }
        ctx.stroke();
      }
    }
  }, [setupContext]);

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    if (!shell || !canvas) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      if (!rect.width || !rect.height) return;
      const prevW = sizeRef.current.w;
      if (prevW && prevW !== rect.width) {
        const s = rect.width / prevW;
        strokesRef.current = strokesRef.current.map((stroke) =>
          stroke.map((p) => ({ x: p.x * s, y: p.y * s }))
        );
      }
      sizeRef.current = { w: rect.width, h: rect.height };
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      redraw();
    });
    observer.observe(shell);
    return () => observer.disconnect();
  }, [redraw]);

  useImperativeHandle(ref, () => ({
    clear() {
      strokesRef.current = [];
      setHasInk(false);
      onInkChange(false);
      redraw();
    },
    getStrokes() {
      return strokesRef.current.map((stroke) => stroke.slice());
    },
  }));

  const pointFromClient = (clientX: number, clientY: number): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: Math.min(Math.max(clientX - rect.left, 0), rect.width),
      y: Math.min(Math.max(clientY - rect.top, 0), rect.height),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // some browsers refuse capture for synthetic or already-lifted pointers
    }
    drawingRef.current = true;
    const p = pointFromClient(e.clientX, e.clientY);
    strokesRef.current.push([p]);
    setHasInk(true);
    onInkChange(true);
    const ctx = setupContext();
    if (ctx) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, PAD_LINE_WIDTH / 2, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    if (!stroke) return;
    const native = e.nativeEvent as PointerEvent & {
      getCoalescedEvents?: () => PointerEvent[];
    };
    const events = native.getCoalescedEvents
      ? native.getCoalescedEvents()
      : [native];
    const ctx = setupContext();
    for (const ev of events.length ? events : [native]) {
      const prev = stroke[stroke.length - 1];
      const p = pointFromClient(ev.clientX, ev.clientY);
      stroke.push(p);
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
    }
  };

  const endStroke = () => {
    drawingRef.current = false;
  };

  const clearPad = () => {
    strokesRef.current = [];
    setHasInk(false);
    onInkChange(false);
    redraw();
  };

  return (
    <PadShell ref={shellRef}>
      <PadBaseline />
      <PadMark aria-hidden="true">✕</PadMark>
      <PadCaption aria-hidden="true">SIGN ABOVE THE LINE</PadCaption>
      <PadCanvas
        ref={canvasRef}
        role="img"
        aria-label="Signature pad. Draw your signature with a finger, stylus, or mouse."
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />
      {hasInk && (
        <PadClear type="button" onClick={clearPad}>
          CLEAR
        </PadClear>
      )}
    </PadShell>
  );
});

/* --------------------------- certificate drawing -------------------------- */

const CERT_W = 1680;
const CERT_H = 1188;
const CERT_PAPER = '#FBF8F2';
const CERT_MUTED = '#6B6560';
const CERT_FAINT = '#B9B2A9';
const CERT_RED = '#C23028';

const FONT_SPECS = [
  '400 20px "Fira Code"',
  '500 15px "Fira Code"',
  '400 17px "Fira Code"',
  'italic 400 38px "Cormorant Garamond"',
  '600 78px "Cormorant Garamond"',
  '600 58px "Cormorant Garamond"',
  '700 56px "Cormorant Garamond"',
  '400 33px "Cormorant Garamond"',
  '300 27px "DM Sans"',
];

function setLetterSpacing(ctx: CanvasRenderingContext2D, px: number) {
  // Not yet in the TS DOM lib; supported by all current browsers, harmless otherwise.
  (ctx as unknown as { letterSpacing: string }).letterSpacing = `${px}px`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawArcText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  radius: number,
  centerAngle: number,
  inward: boolean
) {
  const chars = Array.from(text);
  const widths = chars.map((c) => ctx.measureText(c).width);
  const gap = 2.5;
  const total =
    widths.reduce((a, b) => a + b, 0) + gap * Math.max(chars.length - 1, 0);
  const totalAngle = total / radius;
  let travelled = 0;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  chars.forEach((c, i) => {
    const centerDist = travelled + widths[i] / 2;
    const angle = inward
      ? centerAngle + totalAngle / 2 - centerDist / radius
      : centerAngle - totalAngle / 2 + centerDist / radius;
    ctx.save();
    ctx.translate(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
    ctx.rotate(angle + (inward ? -Math.PI / 2 : Math.PI / 2));
    ctx.fillText(c, 0, 0);
    ctx.restore();
    travelled += widths[i] + gap;
  });
  ctx.restore();
}

function drawSeal(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const R = 97;
  ctx.save();
  ctx.fillStyle = CERT_RED;
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * R, cy + Math.sin(a) * R, 11, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = CERT_PAPER;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy, 86, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 54, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = CERT_PAPER;
  ctx.font = '500 15px "Fira Code"';
  drawArcText(ctx, 'THE WHIMPATHETIC OATH', cx, cy, 70, -Math.PI / 2, false);
  drawArcText(ctx, 'DULY SWORN', cx, cy, 70, Math.PI / 2, true);
  ctx.beginPath();
  ctx.arc(cx - 70, cy, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx + 70, cy, 2.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '700 56px "Cormorant Garamond"';
  ctx.fillText('W', cx, cy - 6);
  ctx.font = '500 12px "Fira Code"';
  ctx.fillText('MMXXVI', cx, cy + 32);
  ctx.restore();
}

function drawSignature(
  ctx: CanvasRenderingContext2D,
  strokes: Stroke[],
  centerX: number,
  baselineY: number,
  maxW: number,
  maxH: number
) {
  const points = strokes.flat();
  if (!points.length) return;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const bw = Math.max(maxX - minX, 1);
  const bh = Math.max(maxY - minY, 1);
  const scale = Math.min(maxW / bw, maxH / bh, 6);
  const dw = bw * scale;
  const dh = bh * scale;
  const ox = centerX - dw / 2;
  const oy = baselineY - dh;

  ctx.save();
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.min(Math.max(PAD_LINE_WIDTH * scale, 2.5), 6.5);
  for (const stroke of strokes) {
    const mapped = stroke.map((p) => ({
      x: ox + (p.x - minX) * scale,
      y: oy + (p.y - minY) * scale,
    }));
    if (mapped.length === 1) {
      ctx.beginPath();
      ctx.arc(mapped[0].x, mapped[0].y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(mapped[0].x, mapped[0].y);
      for (let i = 1; i < mapped.length; i++) {
        ctx.lineTo(mapped[i].x, mapped[i].y);
      }
      ctx.stroke();
    }
  }
  ctx.restore();
}

interface CertificateInput {
  name: string;
  strokes: Stroke[];
  dateLong: string;
  serial: string;
}

async function renderCertificate({
  name,
  strokes,
  dateLong,
  serial,
}: CertificateInput): Promise<string> {
  try {
    await Promise.all(FONT_SPECS.map((spec) => document.fonts.load(spec)));
  } catch {
    // fall back to whatever fonts are available
  }

  const canvas = document.createElement('canvas');
  canvas.width = CERT_W;
  canvas.height = CERT_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser.');
  const cx = CERT_W / 2;

  ctx.fillStyle = CERT_PAPER;
  ctx.fillRect(0, 0, CERT_W, CERT_H);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.strokeRect(54, 54, CERT_W - 108, CERT_H - 108);
  ctx.strokeStyle = CERT_FAINT;
  ctx.lineWidth = 1;
  ctx.strokeRect(70.5, 70.5, CERT_W - 141, CERT_H - 141);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = CERT_MUTED;
  ctx.font = '400 20px "Fira Code"';
  setLetterSpacing(ctx, 4);
  ctx.fillText(`NO. ${serial} · VALID ONLY THE DAY OF SWEARING`, cx, 172);
  setLetterSpacing(ctx, 0);

  ctx.fillStyle = INK;
  ctx.font = 'italic 400 38px "Cormorant Garamond"';
  ctx.fillText('Be it known that', cx, 286);

  let nameSize = 78;
  ctx.font = `600 ${nameSize}px "Cormorant Garamond"`;
  while (ctx.measureText(name).width > 1180 && nameSize > 40) {
    nameSize -= 3;
    ctx.font = `600 ${nameSize}px "Cormorant Garamond"`;
  }
  ctx.fillText(name, cx, 386);

  ctx.font = '300 27px "DM Sans"';
  let y = 458;
  for (const line of wrapText(ctx, CERT_BODY_1, 1150)) {
    ctx.fillText(line, cx, y);
    y += 42;
  }

  y += 46;
  ctx.fillStyle = CERT_RED;
  ctx.font = '600 58px "Cormorant Garamond"';
  setLetterSpacing(ctx, 8);
  ctx.fillText('THE WHIMPATHETIC OATH', cx, y);
  setLetterSpacing(ctx, 0);

  y += 78;
  ctx.fillStyle = INK;
  ctx.font = '300 27px "DM Sans"';
  for (const line of wrapText(ctx, CERT_BODY_2, 1150)) {
    ctx.fillText(line, cx, y);
    y += 42;
  }

  const ruleY = 985;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(170, ruleY + 0.5);
  ctx.lineTo(640, ruleY + 0.5);
  ctx.moveTo(1040, ruleY + 0.5);
  ctx.lineTo(1510, ruleY + 0.5);
  ctx.stroke();

  ctx.fillStyle = CERT_MUTED;
  ctx.font = '400 17px "Fira Code"';
  setLetterSpacing(ctx, 2);
  ctx.fillText('SIGNATURE OF THE SWORN', 405, 1022);
  ctx.fillText('DAY OF SWEARING', 1275, 1022);
  setLetterSpacing(ctx, 0);

  ctx.fillStyle = INK;
  let dateSize = 33;
  ctx.font = `400 ${dateSize}px "Cormorant Garamond"`;
  while (ctx.measureText(dateLong).width > 440 && dateSize > 22) {
    dateSize -= 1;
    ctx.font = `400 ${dateSize}px "Cormorant Garamond"`;
  }
  ctx.fillText(dateLong, 1275, ruleY - 16);

  drawSignature(ctx, strokes, 405, ruleY - 10, 430, 210);
  drawSeal(ctx, cx, 918);

  return canvas.toDataURL('image/png');
}

/* ---------------------------------- page ---------------------------------- */

const Page = styled(motion.div)`
  min-height: 100vh;
  padding: 10rem 2.5rem 6rem;
  max-width: 720px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 7rem 1.25rem 4rem;
  }
`;

const Header = styled.div`
  margin-bottom: 3.5rem;
`;

const Eyebrow = styled(motion.p)`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  color: var(--accent);
  margin-bottom: 1.25rem;
`;

const Title = styled(motion.h1)`
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 7vw, 3.8rem);
  font-weight: 300;
  letter-spacing: -0.01em;
  margin-bottom: 1.25rem;
`;

const Preamble = styled(motion.p)`
  font-size: 0.92rem;
  color: var(--text-muted);
  line-height: 1.7;
  max-width: 56ch;
`;

const Definition = styled(motion.div)`
  margin-bottom: 1.75rem;
  max-width: 56ch;
`;

const DefHeadword = styled.p`
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 0.15rem;
`;

const DefPos = styled.span`
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: 400;
  font-style: italic;
`;

const DefPron = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6rem;
  font-weight: 400;
  white-space: nowrap;
`;

const DefBody = styled.p`
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--text-muted);
`;

const DefEtymology = styled.p`
  font-size: 0.78rem;
  color: var(--text-muted);
  margin-top: 0.3rem;
`;

const OathDocument = styled.div`
  position: relative;
  background: #fbf8f2;
  border: 1px solid var(--border);
  padding: 3rem 3.25rem;

  &::before {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid var(--bg-alt);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.4rem;
  }
`;

const Opening = styled.p`
  font-family: var(--font-display);
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 1.75rem;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const Clause = styled.p`
  font-family: var(--font-display);
  font-size: 1.25rem;
  line-height: 1.6;

  & + & {
    margin-top: 1.1rem;
  }

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const Closing = styled.p`
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.25rem;
  line-height: 1.6;
  margin-top: 1.9rem;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const SectionMark = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin: 3.25rem 0 2.5rem;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  color: var(--text-muted);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
`;

const Field = styled.div`
  margin-bottom: 2rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.6rem;
`;

const NameInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--text);
  border-radius: 0;
  padding: 0.35rem 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text);

  &::placeholder {
    color: #b9b2a9;
    font-weight: 300;
  }

  &:focus {
    outline: none;
    border-bottom-color: var(--accent);
  }
`;

const SwearRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
`;

const DateValue = styled.p`
  font-family: var(--font-display);
  font-size: 1.35rem;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.3rem;
  min-width: 12rem;
`;

const SwearButton = styled.button`
  background: var(--accent);
  color: #f6f2ec;
  border: none;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 1.05rem 2.6rem;
  transition: background 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--accent-hover);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const GhostButton = styled.button`
  background: transparent;
  color: var(--text);
  border: 1px solid var(--border);
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 1.05rem 2.6rem;
  transition: color 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: var(--accent);
    border-color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FinePrint = styled.p`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-top: 1.2rem;
  line-height: 1.7;
`;

const CertFrame = styled.div`
  border: 1px solid var(--border);
  background: #fbf8f2;
  box-shadow: 0 24px 48px -32px rgba(26, 26, 26, 0.4);
`;

const CertImage = styled.img`
  display: block;
  width: 100%;
`;

const CertActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.75rem;
`;

const Oath: React.FC = () => {
  const [name, setName] = useState('');
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState<{
    url: string;
    file: string;
    alt: string;
  } | null>(null);
  const padRef = useRef<SignaturePadHandle>(null);
  const certSectionRef = useRef<HTMLElement>(null);

  const today = new Date();
  const dateShort = `${MONTHS[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;

  const canSwear = name.trim().length > 0 && hasInk;

  const swear = async () => {
    const pad = padRef.current;
    if (!pad || !canSwear || busy) return;
    const strokes = pad.getStrokes();
    if (!strokes.length) return;

    setBusy(true);
    try {
      const sworn = new Date();
      const y = sworn.getFullYear();
      const m = sworn.getMonth();
      const d = sworn.getDate();
      const stamp = `${y}${String(m + 1).padStart(2, '0')}${String(d).padStart(2, '0')}`;
      const serial = `WO-${stamp}-${Math.floor(1000 + Math.random() * 9000)}`;
      const dateLong = `the ${ORDINALS[d - 1]} day of ${MONTHS[m]}, ${y}`;
      const url = await renderCertificate({
        name: name.trim(),
        strokes,
        dateLong,
        serial,
      });
      setCert({
        url,
        file: `whimpathetic-oath-${stamp}.png`,
        alt: `Certificate recording that ${name.trim()} took the Whimpathetic Oath on ${MONTHS[m]} ${d}, ${y}.`,
      });
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!cert) return;
    const a = document.createElement('a');
    a.href = cert.url;
    a.download = cert.file;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const reset = () => {
    setCert(null);
    setName('');
    setHasInk(false);
  };

  return (
    <Page
      initial="initial"
      animate="animate"
      exit="exit"
      variants={fade}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <Eyebrow variants={fade}>
          TO BE ADMINISTERED BEFORE THE ASKING OF A FAVOR
        </Eyebrow>
        <Title variants={fade}>The Whimpathetic Oath</Title>
        <Definition variants={fade}>
          <DefHeadword>
            whimpathetic <DefPos>(adj.)</DefPos>{' '}
            <DefPron>/ˌwɪm·pəˈθɛt·ɪk/</DefPron>
          </DefHeadword>
          <DefBody>
            Moved to kindness by whim, inclination, rather than by obligation;
            disposed to grant a favor.
          </DefBody>
          <DefEtymology>
            <em>Blend of</em> whim <em>+</em> empathetic.
          </DefEtymology>
        </Definition>
        <Preamble variants={fade}>
          The oath below is to be taken, freely and in earnest, by any person
          who would ask a favor of Aaron Leslie. Read the words in full. If you
          find that you can keep them, sign where indicated. The oath binds for
          the day on which it is sworn; a new favor calls for a new swearing.
        </Preamble>
      </Header>

      <AnimatePresence mode="wait" initial={false}>
        {cert ? (
          <motion.section
            key="certificate"
            ref={certSectionRef}
            variants={fade}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.4 }}
            onAnimationComplete={(definition) => {
              if (definition === 'animate') {
                certSectionRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }
            }}
          >
            <SectionMark>LET THE RECORD SHOW</SectionMark>
            <CertFrame>
              <CertImage src={cert.url} alt={cert.alt} />
            </CertFrame>
            <CertActions>
              <SwearButton type="button" onClick={download}>
                Download certificate
              </SwearButton>
              <GhostButton type="button" onClick={reset}>
                Swear it anew
              </GhostButton>
            </CertActions>
            <FinePrint>
              Valid only for the day of swearing. A screenshot serves equally
              well.
            </FinePrint>
          </motion.section>
        ) : (
          <motion.section
            key="form"
            variants={fade}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.4 }}
          >
            <OathDocument>
              <Opening>
                I do solemnly affirm, of my own free will and accord:
              </Opening>
              {CLAUSES.map((clause) => (
                <Clause key={clause}>{clause}</Clause>
              ))}
              <Closing>
                This I swear, freely, whimpathetically, and without
                reservation.
              </Closing>
            </OathDocument>

            <SectionMark>THE SIGNING</SectionMark>

            <Field>
              <FieldLabel htmlFor="oath-name">Name of the sworn</FieldLabel>
              <NameInput
                id="oath-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Given name and surname"
                autoComplete="name"
              />
            </Field>

            <Field>
              <FieldLabel as="span">Signature</FieldLabel>
              <SignaturePad ref={padRef} onInkChange={setHasInk} />
            </Field>

            <SwearRow>
              <div>
                <FieldLabel as="span">Day of swearing</FieldLabel>
                <DateValue>{dateShort}</DateValue>
              </div>
              <SwearButton
                type="button"
                onClick={swear}
                disabled={!canSwear || busy}
              >
                {busy ? 'Administering…' : 'I so swear'}
              </SwearButton>
            </SwearRow>
            <FinePrint>
              A certificate will issue upon swearing. It cannot issue without
              both a printed name and a signature.
            </FinePrint>
          </motion.section>
        )}
      </AnimatePresence>
    </Page>
  );
};

export default Oath;
